import { parse, contrast, ColorSpace, sRGB, HSL } from 'colorjs.io/fn'
import { useMainStore } from '../store'

// Register color spaces for parsing and converting
ColorSpace.register(sRGB) // Can parse keywords and hex colors(sRGB)
ColorSpace.register(HSL) // Can parse HSL colors

export function useColorPalette() {
  const palette = {}
  const cssVariables = {}
  const store = useMainStore()

  for (const [key, value] of Object.entries(store.themeVariables)) {
    cssVariables[key] = value
  }

  if (store.theme?.palette) {
    store.theme.palette.forEach((color) => {
      palette[color.id] = color.name
    })
  }

  const textColorLight = parse('#FFFFFF') // white
  const textColorDark = parse(cssVariables[`--gray-800`] || '#000000') // if undefined, use black

  const backgroundColorClass = function (colorId) {
    if (palette[colorId]) {
      return `bg-${palette[colorId]}`
    }

    return bgLegacyMap[colorId] || bgLegacyMap['white']
  }

  const textColorClass = function (
    colorId,
    backgroundColorId,
    colorIdForLightBg, // Used if colorId is null and the background is light
    colorIdForDarkBg, // Used if colorId is null and the background is dark
  ) {
    const bgId = backgroundColorId || 'white'
    if (!colorId) {
      const darkBg = isBackgroundDark(bgId)
      if (darkBg) {
        return (
          palette[colorIdForDarkBg] ||
          textLegacyMap[colorIdForDarkBg] ||
          textLegacyMap['white']
        )
      } else {
        return (
          palette[colorIdForLightBg] ||
          textLegacyMap[colorIdForLightBg] ||
          textLegacyMap['text-default']
        )
      }
    }

    if (palette[colorId]) {
      return `text-${palette[colorId]}`
    }

    return textLegacyMap[colorId] || textLegacyMap['text-default']
  }

  const colorCustomClass = function (
    colorId,
    backgroundColorId,
    classPrefix = '',
  ) {
    const textClass = textColorClass(colorId, backgroundColorId)
    return textClass.replace(/^text-/, classPrefix)
  }

  const colorName = function (colorId, backgroundColorId) {
    const bgId = backgroundColorId || 'white'
    if (!colorId) {
      const darkBg = isBackgroundDark(bgId)
      if (darkBg) {
        return 'white'
      } else {
        return 'gray-dark'
      }
    }

    return palette[colorId] || colorId
  }

  const isBackgroundDark = function (colorId) {
    const bgColorClass = backgroundColorClass(colorId)
    const hexValue = cssVariables[`--${bgColorClass.substring(3)}`]
    if (hexValue) {
      const bgColor = parse(hexValue)
      const contrastWithDarkText = contrast(bgColor, textColorDark, 'DeltaPhi')
      const contrastWithLightText = contrast(
        bgColor,
        textColorLight,
        'DeltaPhi',
      )
      if (contrastWithDarkText >= contrastWithLightText) {
        return false
      } else {
        return true
      }
    }
  }

  const contrastingGrey = function (bgColorId) {
    const bgColorClass = backgroundColorClass(bgColorId)
    const hexValue = cssVariables[`--${bgColorClass.substring(3)}`]
    if (hexValue) {
      const bgColor = parse(hexValue)

      let grayShade = null

      const baseLightness = 55
      const minContrast = 3

      // Try darker greys by lowering lightness from 55 down to 0
      for (let lightness = baseLightness; lightness >= 0; lightness -= 5) {
        const candidate = parse(`hsl(0, 0%, ${lightness}%)`)
        const constrastRatio = contrast(bgColor, candidate, 'WCAG21')
        if (constrastRatio >= minContrast) {
          grayShade = candidate
          break
        }
      }

      // If no darker grey works, try lighter greys from 60 up to 100
      if (!grayShade) {
        for (
          let lightness = baseLightness + 5;
          lightness <= 100;
          lightness += 5
        ) {
          const candidate = parse(`hsl(0, 0%, ${lightness}%)`)
          const constrastRatio = contrast(bgColor, candidate, 'WCAG21')
          if (constrastRatio >= minContrast) {
            grayShade = candidate
            break
          }
        }
      }

      if (grayShade) {
        return `hsl(0, 0%, ${grayShade.coords[2]}%)`
      }
      return ''
    }

    return ''
  }

  return {
    backgroundColorClass,
    textColorClass,
    colorCustomClass,
    colorName,
    isBackgroundDark,
    contrastingGrey,
  }
}

const bgLegacyMap = {
  default: 'bg-default',
  gray: 'bg-gray-100',
  'gray-dark': 'bg-gray-800',
  white: 'bg-white',
  black: 'bg-black',
  transparent: 'bg-transparent',
  'primary-light': 'bg-primary-100',
  primary: 'bg-primary-500',
  'primary-dark': 'bg-primary-900',
  'secondary-light': 'bg-secondary-100',
  secondary: 'bg-secondary-500',
  'secondary-dark': 'bg-secondary-900',
  'tertiary-light': 'bg-tertiary-100',
  tertiary: 'bg-tertiary-500',
  'tertiary-dark': 'bg-tertiary-900',
  'fourth-light': 'bg-fourth-100',
  fourth: 'bg-fourth-500',
  'fourth-dark': 'bg-fourth-900',
}

const textLegacyMap = {
  default: 'text-default',
  light: 'text-gray-100',
  dark: 'text-gray-900',
  black: 'text-black',
  transparent: 'text-transparent',
  'text-default': 'text-gray-800',
  'text-lighter': 'text-gray-700',
  'text-darker': 'text-gray-900',
  gray: 'text-gray-200',
  'gray-dark': 'text-gray-800',
  white: 'text-white',
  'primary-light': 'text-primary-100',
  primary: 'text-primary-500',
  'primary-dark': 'text-primary-900',
  'secondary-light': 'text-secondary-100',
  secondary: 'text-secondary-500',
  'secondary-dark': 'text-secondary-900',
  'tertiary-light': 'text-tertiary-100',
  tertiary: 'text-tertiary-500',
  'tertiary-dark': 'text-tertiary-900',
  'fourth-light': 'text-fourth-100',
  fourth: 'text-fourth-500',
  'fourth-dark': 'text-fourth-900',
}
